import React, { useEffect, useState } from 'react';
import { Bookmark, X, Star } from 'lucide-react';

// Floating pop-out card with an arrow pointing at the URL bar. Shown once
// after clinic sign-up so the clinic saves their unique sign-in URL. Renders
// nothing when a `mdai_dismissed_bookmark_hint` flag is set in localStorage
// so it doesn't nag the same user twice.
//
// Auto-dismisses after 45s or on the ✕ click.

const DISMISSED_KEY = 'mdai_dismissed_bookmark_hint_v1';
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function Kbd({ children }) {
  return (
    <kbd style={{
      display: 'inline-block',
      padding: '2px 7px',
      margin: '0 2px',
      fontFamily: 'inherit',
      fontSize: 11,
      fontWeight: 700,
      color: '#0f172a',
      background: '#ffffff',
      border: '1px solid #cbd5e1',
      borderBottomWidth: 2,
      borderRadius: 4,
      lineHeight: 1,
    }}>{children}</kbd>
  );
}

function BookmarkHint({ show = true, onClose }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!show) return undefined;
    try {
      if (localStorage.getItem(DISMISSED_KEY)) return undefined;
    } catch { /* ignore */ }
    // Small delay so the user sees the login form settle first.
    const t = setTimeout(() => setVisible(true), 500);
    const auto = setTimeout(() => dismiss(), 45_000);
    return () => { clearTimeout(t); clearTimeout(auto); };
  }, [show]);

  function dismiss() {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
    onClose?.();
  }

  if (!visible) return null;

  return (
    <>
      {/* Arrow triangle pointing up towards the browser URL bar. */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 10,
          right: 40,
          zIndex: 1001,
          width: 0,
          height: 0,
          borderLeft: '10px solid transparent',
          borderRight: '10px solid transparent',
          borderBottom: '14px solid #f59e0b',
          filter: 'drop-shadow(0 -2px 3px rgba(0,0,0,0.15))',
          animation: 'mdai-hint-bounce 1.4s ease-in-out infinite',
        }}
      />

      <div
        role="dialog"
        aria-label="Bookmark this page"
        style={{
          position: 'fixed',
          top: 30,
          right: 20,
          zIndex: 1000,
          maxWidth: 340,
          background: 'linear-gradient(180deg,#fffbeb 0%,#fef3c7 100%)',
          border: '1px solid #f59e0b',
          borderRadius: 12,
          boxShadow: '0 12px 30px rgba(15,23,42,0.20)',
          padding: '16px 18px 14px 18px',
          color: '#78350f',
          fontSize: 13,
          lineHeight: 1.55,
        }}
      >
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            color: '#78350f',
            padding: 4,
          }}
        >
          <X size={14} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Bookmark size={18} color="#b45309" />
          <strong style={{ fontSize: 14, color: '#78350f' }}>Bookmark this page</strong>
          <Star size={12} color="#f59e0b" />
        </div>

        <p style={{ margin: '0 0 10px 0' }}>
          Save this page as a bookmark or desktop shortcut so you can log in with a single click.
        </p>

        <div style={{
          background: '#ffffff',
          border: '1px solid #fde68a',
          borderRadius: 8,
          padding: 10,
          fontSize: 12,
          color: '#0f172a',
        }}>
          <div style={{ marginBottom: 6, fontWeight: 600 }}>Press:</div>
          <div>
            {IS_MAC ? (
              <><Kbd>⌘</Kbd> + <Kbd>D</Kbd></>
            ) : (
              <><Kbd>Ctrl</Kbd> + <Kbd>D</Kbd></>
            )}
            <span style={{ marginLeft: 8, color: '#475569' }}>on your keyboard</span>
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
            Or drag the URL bar icon to your desktop for a shortcut.
          </div>
        </div>

        <button
          type="button"
          onClick={dismiss}
          className="btn btn-primary btn-block"
          style={{ marginTop: 12, background: '#b45309', border: 0, color: 'white' }}
        >
          Got it, thanks
        </button>
      </div>

      <style>{`
        @keyframes mdai-hint-bounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(4px); }
        }
      `}</style>
    </>
  );
}

export default BookmarkHint;
